// Pagina obbligatoria per account admin/superadmin con password temporanea
// (mustChangePassword=true). AuthGuard reindirizza qui finché il flag non
// viene azzerato dal backend dopo un cambio password riuscito.
// Non riguarda mai gli utenti normali (role=user): il flag non è mai
// impostato dai loro flussi di registrazione.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Alert, Spinner } from 'react-bootstrap';
import { Eye, EyeSlash, Lock } from 'react-bootstrap-icons';
import { useAuth } from '../../../contexts/AuthContext';
import { createUserService } from '../../../services';
import {
  isPasswordValid,
  validatePassword,
} from '../../../utils/passwordvalidation';
import './ForcePasswordChange.css';

const ROLE_HOME: Record<string, string> = {
  admin: '/admin',
  superadmin: '/superadmin',
};

const ForcePasswordChange: React.FC = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const userService = createUserService(user?.id);

  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const validation = validatePassword(password);
  const passwordsMatch = password === confirm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      setError('Inserisci la password temporanea che ti è stata comunicata.');
      return;
    }
    if (!isPasswordValid(validation)) {
      setError('La nuova password non rispetta i requisiti di sicurezza.');
      return;
    }
    if (!passwordsMatch) {
      setError('Le password non coincidono.');
      return;
    }

    setLoading(true);
    try {
      await userService.changePassword(currentPassword, password);
      updateUser({ mustChangePassword: false });
      navigate(ROLE_HOME[user?.role ?? ''] ?? '/', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Errore durante il cambio password. Riprova.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fpc-page">
      <div className="fpc-card">
        <div className="fpc-icon">🔒</div>
        <h1 className="fpc-title">Imposta una nuova password</h1>
        <p className="fpc-sub">
          Per motivi di sicurezza devi scegliere una nuova password prima di
          continuare.
        </p>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setError('')}
            className="small mb-3"
          >
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} noValidate>
          <Form.Label className="fpc-label" htmlFor="fpc-current">
            Password temporanea
          </Form.Label>
          <div className="fpc-input-wrap">
            <span className="fpc-input-icon">
              <Lock size={16} />
            </span>
            <Form.Control
              id="fpc-current"
              type={showCurrent ? 'text' : 'password'}
              placeholder="••••••••"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="fpc-toggle"
              onClick={() => setShowCurrent((v) => !v)}
              aria-label="Mostra/nascondi password temporanea"
            >
              {showCurrent ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>

          <Form.Label className="fpc-label" htmlFor="fpc-password">
            Nuova password
          </Form.Label>
          <div className="fpc-input-wrap">
            <span className="fpc-input-icon">
              <Lock size={16} />
            </span>
            <Form.Control
              id="fpc-password"
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <button
              type="button"
              className="fpc-toggle"
              onClick={() => setShowPwd((v) => !v)}
              aria-label="Mostra/nascondi nuova password"
            >
              {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {password.length > 0 && !isPasswordValid(validation) && (
            <div className="fpc-hints">
              {[
                { ok: validation.hasMinLength, text: 'Almeno 8 caratteri' },
                { ok: validation.hasUpperCase, text: 'Una lettera maiuscola' },
                { ok: validation.hasLowerCase, text: 'Una lettera minuscola' },
                { ok: validation.hasNumber, text: 'Un numero' },
                { ok: validation.hasSymbol, text: 'Un simbolo (!@#$%...)' },
              ].map(({ ok, text }) => (
                <div
                  key={text}
                  className={`fpc-hint-item ${ok ? 'fpc-hint--ok' : ''}`}
                >
                  <i
                    className={`bi ${ok ? 'bi-check-circle-fill' : 'bi-x-circle'} me-1`}
                  />
                  {text}
                </div>
              ))}
            </div>
          )}

          <Form.Label className="fpc-label" htmlFor="fpc-confirm">
            Conferma nuova password
          </Form.Label>
          <div className="fpc-input-wrap">
            <span className="fpc-input-icon">
              <Lock size={16} />
            </span>
            <Form.Control
              id="fpc-confirm"
              type={showConfirm ? 'text' : 'password'}
              placeholder="••••••••"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className={
                confirm.length > 0 && !passwordsMatch ? 'is-invalid' : ''
              }
            />
            <button
              type="button"
              className="fpc-toggle"
              onClick={() => setShowConfirm((v) => !v)}
              aria-label="Mostra/nascondi conferma"
            >
              {showConfirm ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {confirm.length > 0 && !passwordsMatch && (
            <p className="fpc-match-error">Le password non coincidono.</p>
          )}

          <button
            type="submit"
            className="fpc-btn-primary mt-3"
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Aggiornamento...
              </>
            ) : (
              'Conferma nuova password'
            )}
          </button>
        </Form>
      </div>
    </div>
  );
};

export default ForcePasswordChange;
